import React from "react";
import { Link } from "react-router-dom";

import {
  Avatar,
  IconButton,
  makeStyles,
  Theme,
  Tooltip
} from "@material-ui/core";

import { AvatarGroup, Skeleton } from "@material-ui/lab";
import { BiUserPlus } from "react-icons/bi";

import HStack from "modules/core/components/HStack";
import Can from "modules/user/config/permissions/Can";

import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";
import { useDashboardUsersQuery } from "modules/core/hooks/queries/useDashboardUsersQuery";

import { userPrivateRoutesConstants } from "modules/user/routes/private/routesConstants";

const useStyles = makeStyles<Theme, { avatarSize?: number }>(theme => ({
  avatar: {
    width: ({ avatarSize }) => avatarSize,
    height: ({ avatarSize }) => avatarSize
  },

  addBtn: {
    border: `1px dashed ${theme.palette.divider}`,
    width: 40,
    height: 40
  },

  loadingCircle: {
    "&:not(:first-child)": {
      marginLeft: -8
    }
  }
}));

type UsersIndicatorProps = {
  avatarSize?: number;
  hideAddButton?: boolean;
};

const UsersIndicator = (props: UsersIndicatorProps) => {
  const { avatarSize, hideAddButton } = props;

  const classes = useStyles({ avatarSize });

  const { isSingleProject, selectedProject } = useSelectedProject();

  const { data: users, isLoading } = useDashboardUsersQuery();

  return (
    <HStack gap={10}>
      {isLoading ? (
        <HStack>
          <Skeleton
            variant="circle"
            width={avatarSize ?? 40}
            height={avatarSize ?? 40}
            className={classes.loadingCircle}
          />
          <Skeleton
            variant="circle"
            width={avatarSize ?? 40}
            height={avatarSize ?? 40}
            className={classes.loadingCircle}
          />
          <Skeleton
            variant="circle"
            width={avatarSize ?? 40}
            height={avatarSize ?? 40}
            className={classes.loadingCircle}
          />
        </HStack>
      ) : (
        <AvatarGroup max={3} classes={{ avatar: classes.avatar }}>
          {users?.map(user => (
            <Tooltip key={user.id} title={`${user.name} ${user.surname}`}>
              <Avatar
                src={user.avatar || ""}
                alt={user.name}
                className={classes.avatar}
              >
                {user.name?.at(0)}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      )}

      {!hideAddButton && (
        <Can
          I="create"
          a="user"
          in={isSingleProject ? selectedProject?.id : undefined}
          usingBestRole={!isSingleProject}
        >
          <Tooltip title="Add User">
            <IconButton
              className={classes.addBtn}
              component={Link}
              to={
                userPrivateRoutesConstants.user_management.href(
                  isSingleProject ? selectedProject?.id || "" : "all"
                ) + "&add_user=true"
              }
            >
              <BiUserPlus />
            </IconButton>
          </Tooltip>
        </Can>
      )}
    </HStack>
  );
};

export default React.memo(UsersIndicator);
